import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { useWatch } from 'react-hook-form';
import { ImageFile } from 'react-dropzone';
import { WppFileUpload, WppInlineMessage } from 'buildingBlocks';
import { dataUploader } from 'containers/StrategyAnalytics/components/View/DataUploader/style';
import { FileStates as FileStatesEnum } from 'containers/StrategyAnalytics/components/View/DataUploader/constants';
import { BULK_UPLOAD_FILE_TEMPLATE_NAME, BULK_UPLOAD_FILE_TEMPLATE_PATH, FILE_PARSE_ERROR, configFileConfigBulkUploader } from 'containers/BulkCreateStrategyWizard/steps/AttachFlights/constants';
import { useBulkCreateAttachModalContext } from 'containers/BulkCreateStrategyWizard/steps/AttachFlights/contexts/BulkCreateAttachModalProvider';
import { bulkUploaderStyles } from 'containers/BulkCreateStrategyWizard/steps/AttachFlights/style';
import { selectedStrategiesPanel } from 'containers/StrategiesList/style';

const { grid, fileUploaderStyle } = dataUploader;
const { memberFieldLabel, uploaderSection } = bulkUploaderStyles;
const { editModalBanner } = selectedStrategiesPanel;

type Props = {
  setBulkFlightIds: (ids: Array<number>) => void
  setErrMessage: (err: string) => void
  bulkUploadModalOpen: boolean
  shouldShowFileErr: boolean
};

const FileUploadZone = ({ setBulkFlightIds, setErrMessage, bulkUploadModalOpen, shouldShowFileErr }: Props) => {
  const { flightCandidateInfo: { loadingFlightCandidates } } = useBulkCreateAttachModalContext();
  const member = useWatch({ name: 'member' });
  const [reader, setReader] = useState<FileReader>(new FileReader());
  const [fileUploadState, setFileUploadState] = useState<FileStatesEnum>(FileStatesEnum.initial);

  const disableDrop = _.isEqual(FileStatesEnum.failed, fileUploadState) || !member || loadingFlightCandidates;
  const fileUploadRef = useRef<HTMLWppFileUploadElement>(null);

  const resetFileState = () => {
    setFileUploadState(FileStatesEnum.initial);
  };

  useEffect(() => {
    if (!bulkUploadModalOpen) {
      fileUploadRef.current?.reset();
      setReader(new FileReader());
      resetFileState();
    }
  }, [bulkUploadModalOpen]);

  const onDrop = (accepted: Array<ImageFile>) => {
    setFileUploadState(FileStatesEnum.initial);
    try {
      const file = _.first(accepted);
      if (file) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        let flightExtIds;
        reader.onload = () => {
          const readerResult = reader.result;
          if (!readerResult) {
            return;
          }
          if (fileExtension === 'json') {
            const data = readerResult ? JSON.parse(readerResult as string) : [];
            flightExtIds = _.compact(_.map(data, (item) => item.externalID));
          }
          if (fileExtension === 'xlsx') {
            const workbook = XLSX.read(readerResult, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            flightExtIds = _.compact(_.map(jsonData, (item: any) => _.toString(item.externalID)));
          }

          if (flightExtIds.length === 0) {
            resetFileState();
            setErrMessage(FILE_PARSE_ERROR);
            return;
          }
          setBulkFlightIds(flightExtIds);
        };
        if (fileExtension === 'json') {
          reader.readAsText(file);
        } else if (fileExtension === 'xlsx') {
          reader.readAsArrayBuffer(file);
        }
      } else {
        setErrMessage('');
      }
    } catch (error) {
      console.error(FILE_PARSE_ERROR, error);
      resetFileState();
      setErrMessage(FILE_PARSE_ERROR);
    }
  };

  const handleFileUploadChange = (event: CustomEvent) => {
    setErrMessage('');
    const fileUploadValue = event.detail.value;
    onDrop(fileUploadValue);
  };

  const onActionBtnClick = () => {
    const link = document.createElement('a');
    link.href = BULK_UPLOAD_FILE_TEMPLATE_PATH;
    link.download = BULK_UPLOAD_FILE_TEMPLATE_NAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ ...uploaderSection, ...grid, ...memberFieldLabel }}>
      <WppInlineMessage
        size="l"
        type="information"
        hideCloseBtn
        style={editModalBanner}
        message="Follow our template format to ensure successful ID uploads"
        actionBtnText="Download Template"
        onWppClickActionBtn={onActionBtnClick}
        className="bulkUploadSampleFile"
      />
      <WppFileUpload
        key={bulkUploadModalOpen ? 'open' : 'closed'}
        disabled={disableDrop}
        onWppChange={handleFileUploadChange}
        ref={fileUploadRef}
        style={fileUploaderStyle}
        onWppFileUploadItemDelete={resetFileState}
        acceptConfig={configFileConfigBulkUploader}
        className={shouldShowFileErr ? 'bulk-file-upload-zone' : ''}
        size={50}
        maxFiles={1}
        locales={{
          label: 'Choose a file',
          text: 'to upload or drag it here',
          info: () => 'Only .xlsx or .json file at 50 MB or less',
          sizeError: 'File exceeds size limit',
          formatError: 'Unsupported file format. Please upload a xlsx or .json file.',
        }}
      />
    </div>
  );
};

export default FileUploadZone;
